import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-all duration-300 hover:ring-2 hover:ring-ring focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 min-h-[44px] min-w-[44px]",
  {
    variants: {
      variant: {
        default: "bg-background text-foreground border-2 border-input hover:border-accent hover:ring-2 hover:ring-accent/50 hover:text-accent shadow-soft hover:shadow-boho active:bg-primary active:text-primary-foreground transition-all",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90 shadow-soft",
        outline:
          "border-2 border-input bg-background text-foreground hover:bg-accent/10 hover:text-accent hover:border-accent hover:ring-2 hover:ring-accent/50 shadow-soft hover:shadow-boho active:bg-primary active:text-primary-foreground transition-all",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/90 shadow-soft",
        ghost: "text-muted-foreground hover:bg-accent/10 hover:text-accent",
        link: "text-primary underline-offset-4 hover:underline hover:text-primary-glow",
        boho: "bg-accent text-accent-foreground hover:bg-accent/90 hover:shadow-glow transform hover:-translate-y-1 border border-accent/50 hover:shadow-boho",
        "add-to-cart": "bg-gradient-conversion text-accent-foreground hover:bg-accent/90 hover:shadow-conversion font-bold border-2 border-accent hover:border-accent/80 transform hover:scale-105",
        selected: "bg-primary text-primary-foreground hover:bg-primary/90 hover:text-primary-foreground shadow-soft",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
